/**
 * Aba "Historico" do TaskDetailModal.
 * Lista audit entries com texto humanizado.
 */
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Plus,
  Pencil,
  CheckCircle,
  Mail,
  Forward,
  Activity,
} from 'lucide-react';

import { apiClient, type TaskHistoryEntry } from '@/lib/api-client';

export interface HistoryTabProps {
  taskId: string;
}

function humanizeAction(entry: TaskHistoryEntry): string {
  const actor = entry.actorName ?? 'Sistema';
  switch (entry.action) {
    case 'task.created':
    case 'tasks.task.created':
      return `Tarefa criada por ${actor}`;
    case 'task.updated':
    case 'tasks.task.updated':
      return `Tarefa atualizada por ${actor}`;
    case 'task.completed':
    case 'tasks.task.completed':
      return `Marcada como concluída por ${actor}`;
    case 'task.uncompleted':
    case 'tasks.task.uncompleted':
      return `Reaberta por ${actor}`;
    case 'task.deleted':
    case 'tasks.task.deleted':
      return `Excluída por ${actor}`;
    case 'task.comment.added':
    case 'tasks.task.comment.added':
      return `Comentário adicionado por ${actor}`;
    case 'tasks.task.email_sent':
      return `E-mail enviado por ${actor}`;
    case 'tasks.task.forwarded':
      return `Tarefa encaminhada por ${actor}`;
    case 'task.assignee.added':
    case 'tasks.task.assignee.added':
      return `Responsável adicionado por ${actor}`;
    case 'task.assignee.removed':
    case 'tasks.task.assignee.removed':
      return `Responsável removido por ${actor}`;
    default:
      return `${entry.action} executado por ${actor}`;
  }
}

function actionIcon(action: string): React.ReactNode {
  if (action.includes('created')) return <Plus className="h-3.5 w-3.5 text-green-500" />;
  if (action.includes('updated')) return <Pencil className="h-3.5 w-3.5 text-blue-500" />;
  if (action.includes('completed') || action.includes('uncompleted'))
    return <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />;
  if (action.includes('email')) return <Mail className="h-3.5 w-3.5 text-indigo-500" />;
  if (action.includes('forward')) return <Forward className="h-3.5 w-3.5 text-orange-500" />;
  return <Activity className="h-3.5 w-3.5 text-[var(--fg-muted)]" />;
}

export function HistoryTab({ taskId }: HistoryTabProps) {
  const { data: entries = [], isLoading, error } = useQuery<TaskHistoryEntry[]>({
    queryKey: ['task', taskId, 'history'],
    queryFn: () => apiClient.tasksGetHistory(taskId),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 px-5 py-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3 animate-pulse">
            <div className="mt-0.5 h-5 w-5 rounded-full bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 w-48 rounded bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]" />
              <div className="h-2.5 w-24 rounded bg-[color-mix(in_oklch,var(--fg)_5%,transparent)]" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <p className="px-5 py-4 text-sm text-red-600">
        Falha ao carregar histórico.
      </p>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="px-5 py-4 text-center text-sm text-[var(--fg-muted)]">
        Nenhum registro no histórico.
      </p>
    );
  }

  // Ordem reversa: mais recente no topo
  const sorted = [...entries].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="flex flex-col gap-0 px-5 py-4">
      {sorted.map((entry) => {
        const relative = (() => {
          try {
            return formatDistanceToNow(parseISO(entry.createdAt), {
              locale: ptBR,
              addSuffix: true,
            });
          } catch {
            return entry.createdAt;
          }
        })();

        const absolute = (() => {
          try {
            return format(parseISO(entry.createdAt), "dd/MM/yyyy 'às' HH:mm", {
              locale: ptBR,
            });
          } catch {
            return entry.createdAt;
          }
        })();

        return (
          <div key={entry.id} className="flex gap-3 py-2.5 border-b border-[var(--border)] last:border-0">
            <div className="mt-0.5 shrink-0">
              {actionIcon(entry.action)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[var(--fg)] leading-snug">
                {humanizeAction(entry)}
              </p>
              <p
                className="mt-0.5 text-[11px] text-[var(--fg-muted)] cursor-default"
                title={absolute}
              >
                {relative}
              </p>

              {/* Metadados opcionais */}
              {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {Object.entries(entry.metadata)
                    .filter(([, v]) => v !== null && v !== undefined && v !== '')
                    .slice(0, 3)
                    .map(([k, v]) => (
                      <span
                        key={k}
                        className="inline-block rounded bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] px-1.5 py-0.5 text-[10px] text-[var(--fg-muted)]"
                      >
                        {k}: {String(v)}
                      </span>
                    ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
